import { useStorage } from '@funtech-int/web-toolkit';
import { useFromBlock, useFeatureFlags, useAuthManager } from '@funtech-int/yango-shared';
import { useSignal } from '@funtech-int/rival-react';

import { TrackingData } from '@kinopoisk-int/ott-trackings-builder/lib/types';

import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { getDeviceUuid } from '@/shared/lib/deviceUuid';
import { useCurrentUrl } from '@/shared/hooks/useCurrentUrl';
import { clientConfig } from '@/config/client';

import { featureFlagsScheme } from '../../../../lib/featureFlags';
import { getDeviceInfo } from '../../analytics/utils/get-device-info';
import { PlayerTrackerData, StreamTrackingData, TrackingDataPart } from '../../types/tracker';
import { getPsid } from '../../../Player/lib/utils/psid';

interface Props<AdditionalKeys extends keyof TrackingData> {
    additionalData?: TrackingDataPart<AdditionalKeys>;
    streamTrackingData: StreamTrackingData | undefined;
}

const { appName, appVersion, productServiceName: configServiceName } = clientConfig;

// 1 - desktop браузер for Mediascope
const DEVICE_TYPE = 1;

const noNull = <T>(value: T | null | undefined) => (value === null ? undefined : value);
const toString = (value: string | number | undefined) =>
    value === undefined ? undefined : String(value);

export const useTrackerData: <AdditionalKeys extends keyof TrackingData>(
    props: Props<AdditionalKeys>
) => PlayerTrackerData = ({ additionalData, streamTrackingData }) => {
    const emptyParams = {
        deviceId: '-',
        platformVersion: '-',
        platformModel: '-',
        platformVendor: '-',
        platformVendorDeviceId: '-',
    };

    const appData = { appName, appVersion };

    const { browser, operationSystem, operationSystemMajorVersion, operationSystemMinorVersion } =
        getDeviceInfo();

    const deviceInfo = {
        platformOs: operationSystem,
        platformOsVersionMajor: operationSystemMajorVersion,
        platformOsVersionMinor: operationSystemMinorVersion,
        browserName: browser?.name,
        browserVer: browser?.version,
        browserVerMajor: browser?.major,
        browserVerMinor: browser?.minor,
    };

    const authManager = useAuthManager();

    const storage = useStorage();

    const deviceUuid = getDeviceUuid(storage);

    const psid = getPsid();

    const profileManager = useProfileManager();
    const localPuid = profileManager.userProfile?.puid;
    const { testIds } = useFeatureFlags(featureFlagsScheme);

    const { kpId, puid, serviceName, ...restStreamTrackingData } = streamTrackingData || {};
    const streamTrackingDataWithDefaults = {
        kpId: toString(kpId),
        puid: toString(puid ?? noNull(localPuid)),
        serviceName: serviceName ?? configServiceName,
        ...restStreamTrackingData,
    };

    const prepareStartupDataTime = Date.now();

    const {
        sessionIds: { globalSessionId, localSessionId: localSessionIdSignal },
    } = useFromBlock();

    const localSessionId = useSignal(localSessionIdSignal);
    const currentUrl = useCurrentUrl();
    return {
        ...emptyParams,
        ...appData,
        ...deviceInfo,
        deviceType: DEVICE_TYPE,
        psid,
        prerollUuid: 'preroll',
        deviceUuid,
        prepareStartupDataTime,
        yandexuid: authManager.yandexuid,
        subProfileId: undefined,
        page: encodeURIComponent(currentUrl.toString()),
        testIds: testIds.join(','),
        globalSessionId,
        localSessionId,
        ...streamTrackingDataWithDefaults,
        ...additionalData,
    };
};
